import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Code2, Palette, Share2, TrendingUp, MapPin, Clock, Hexagon } from "lucide-react";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import LeadForm from "@/components/LeadForm";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { api } from "@/lib/api";
import { BUSINESS, IMAGES, SERVICES, PROCESS_STEPS } from "@/data/content";

const ICONS = { Code2, Palette, Share2, TrendingUp };
const ACCENTS = {
    violet: "text-violet-400 border-violet-500/20 hover:border-violet-500/60",
    cyan: "text-cyan-400 border-cyan-500/20 hover:border-cyan-500/60",
    amber: "text-amber-400 border-amber-500/20 hover:border-amber-500/60",
};

function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
    const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

    const line = (children, delay) => (
        <span className="mask-line">
            <motion.span
                className="block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
            >
                {children}
            </motion.span>
        </span>
    );

    return (
        <section ref={ref} className="relative overflow-hidden pt-32 sm:pt-40 pb-20 lg:pb-28" data-testid="home-hero">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_50%_at_70%_20%,rgba(124,58,237,0.18),transparent_70%)]" />
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(40%_40%_at_15%_80%,rgba(6,182,212,0.10),transparent_70%)]" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
                <motion.div className="lg:col-span-7" style={{ y: yText }}>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 mb-8">
                        <Hexagon className="h-3.5 w-3.5 text-amber-400" />
                        <span className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-slate-300">Full-Service Digital Creative Agency — Long Beach, CA</span>
                    </motion.div>
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-white" data-testid="hero-headline">
                        {line("Where", 0.15)}
                        {line("Innovation", 0.28)}
                        {line(<span className="bg-gradient-to-r from-violet-400 via-fuchsia-400 to-cyan-300 bg-clip-text text-transparent">Thrives.</span>, 0.41)}
                    </h1>
                    <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.7 }}
                        className="mt-6 font-display text-lg md:text-xl font-semibold text-slate-200" data-testid="hero-subheadline">
                        Creative Design. Powerful Websites. Smarter Digital Marketing.
                    </motion.p>
                    <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.78, duration: 0.7 }}
                        className="mt-4 max-w-xl text-base text-slate-400 leading-relaxed">
                        Innovative Fusion Hives helps businesses build distinctive brands, create exceptional digital experiences, and grow their online presence through strategic design, web development, social media management, and digital marketing.
                    </motion.p>
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }}
                        className="mt-9 flex flex-wrap gap-4">
                        <Link to="/contact" data-testid="hero-cta-start-project"
                            className="group inline-flex items-center gap-2 rounded-full bg-violet-600 px-7 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 animate-pulse-glow">
                            Start Your Project <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </Link>
                        <Link to="/portfolio" data-testid="hero-cta-portfolio"
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-semibold text-slate-200 transition-all hover:border-cyan-400/60 hover:text-white">
                            Explore Our Portfolio <ArrowRight className="h-4 w-4" />
                        </Link>
                    </motion.div>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }}
                        className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
                        {["4 Core Services", "6-Step Process", "Long Beach, California"].map((t) => (
                            <span key={t} className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-slate-500">
                                <span className="h-1.5 w-1.5 rotate-45 bg-amber-400" /> {t}
                            </span>
                        ))}
                    </motion.div>
                </motion.div>
                <motion.div className="lg:col-span-5 relative hidden lg:block" style={{ y: yImg }}
                    initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}>
                    <div className="relative rounded-2xl border border-white/10 bg-hive-panel p-2 shadow-[0_24px_80px_rgba(0,0,0,0.5)]">
                        <div className="flex items-center gap-1.5 px-3 py-2">
                            <span className="h-2.5 w-2.5 rounded-full bg-rose-500/70" />
                            <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                        </div>
                        <img src={IMAGES.hero} alt="Modern website design mockup showcasing digital experiences" className="rounded-xl aspect-[4/3] w-full object-cover" loading="eager" />
                    </div>
                    <div className="absolute -bottom-6 -left-8 rounded-xl border border-white/10 bg-[#101625]/90 backdrop-blur px-5 py-4 shadow-xl">
                        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-violet-400">Design + Code + Growth</p>
                        <p className="mt-1 font-display font-bold text-white">One hive. Every discipline.</p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

export default function Home() {
    const [featured, setFeatured] = useState([]);
    useEffect(() => {
        api.get("/portfolio").then((r) => setFeatured(r.data.slice(0, 3))).catch(() => {});
    }, []);

    return (
        <main>
            <Seo path="/" description="Innovative Fusion Hives is a full-service digital agency in Long Beach, CA offering web development, graphic design, social media management, and digital marketing." />
            <Hero />
            <Marquee />

            {/* Trust intro */}
            <section className="py-20 lg:py-32" data-testid="home-intro">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-14 items-center">
                    <Reveal>
                        <div className="relative">
                            <img src={IMAGES.studio} alt="Creative studio team collaborating on digital projects" className="rounded-2xl border border-white/10 aspect-[4/3] object-cover w-full" loading="lazy" />
                            <div className="absolute -bottom-5 -right-3 sm:-right-5 rounded-xl border border-amber-500/30 bg-[#14100a]/95 px-5 py-3">
                                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-amber-400">Where Innovation Thrives</p>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <Eyebrow>The Agency</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">
                            A creative hive built for ambitious businesses.
                        </h2>
                        <p className="mt-5 text-base text-slate-400 leading-relaxed">
                            We are designers, developers, and strategists working as one connected hive. From your first logo to your hundredth campaign, we bring the craft of a boutique studio and the discipline of a growth partner — serving startups, small businesses, and established companies across Long Beach and beyond.
                        </p>
                        <p className="mt-4 text-base text-slate-400 leading-relaxed">
                            No inflated promises. No recycled templates. Just thoughtful strategy, distinctive creative, and digital experiences built to perform.
                        </p>
                        <Link to="/about" data-testid="intro-about-link" className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-violet-200 transition-colors">
                            Learn more about us <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Services bento */}
            <section className="py-20 lg:py-28 border-t border-white/5" data-testid="home-services">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                    <Reveal className="max-w-2xl">
                        <Eyebrow>What We Do</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Four disciplines. One connected hive.</h2>
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
                        {SERVICES.map((s, i) => {
                            const Icon = ICONS[s.icon];
                            const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"];
                            return (
                                <Reveal key={s.slug} delay={i * 0.08} className={spans[i]}>
                                    <Link to={`/services/${s.slug}`} data-testid={`home-service-card-${s.slug}`}
                                        className={`group flex h-full flex-col justify-between rounded-2xl border bg-hive-card p-7 sm:p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(124,58,237,0.15)] ${ACCENTS[s.accent]}`}>
                                        <div>
                                            <div className="flex items-center justify-between">
                                                <Icon className="h-8 w-8" />
                                                <ArrowUpRight className="h-5 w-5 text-slate-600 transition-all group-hover:text-white group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                            </div>
                                            <h3 className="mt-6 font-display text-xl sm:text-2xl font-bold text-white">{s.name}</h3>
                                            <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.short}</p>
                                        </div>
                                        <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">{s.cta} →</p>
                                    </Link>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Featured work */}
            <section className="py-20 lg:py-28 border-t border-white/5" data-testid="home-featured-work">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                    <div className="flex flex-wrap items-end justify-between gap-6">
                        <Reveal>
                            <Eyebrow>Selected Work</Eyebrow>
                            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Featured concepts from the hive.</h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <Link to="/portfolio" data-testid="featured-view-all-link" className="inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-violet-200">
                                View all work <ArrowRight className="h-4 w-4" />
                            </Link>
                        </Reveal>
                    </div>
                    <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {featured.map((p, i) => (
                            <Reveal key={p.id} delay={i * 0.08}>
                                <Link to={`/portfolio/${p.slug}`} data-testid={`featured-project-${p.slug}`}
                                    className="group block overflow-hidden rounded-2xl border border-white/10 bg-hive-card transition-all hover:border-violet-500/50 hover:-translate-y-1">
                                    <div className="relative overflow-hidden aspect-[4/3]">
                                        <img src={p.cover_image} alt={`${p.title} cover`} loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        {p.is_sample && (
                                            <span className="absolute left-3 top-3 rounded-full border border-amber-400/40 bg-black/70 px-3 py-1 font-mono2 text-[10px] uppercase tracking-widest text-amber-300">Concept</span>
                                        )}
                                    </div>
                                    <div className="p-6">
                                        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-cyan-400">{p.category}</p>
                                        <h3 className="mt-2 font-display text-lg font-bold text-white leading-snug">{p.title}</h3>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                    <p className="mt-6 text-xs text-slate-500" data-testid="featured-concept-note">
                        Demonstration concepts shown while real client engagements are added. No fictional clients or results.
                    </p>
                </div>
            </section>

            {/* Process preview */}
            <section className="py-20 lg:py-28 border-t border-white/5 bg-[#0C101A]" data-testid="home-process">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                    <Reveal className="max-w-2xl">
                        <Eyebrow>How We Work</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">A proven path from idea to launch.</h2>
                    </Reveal>
                    <div className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-6">
                        {PROCESS_STEPS.map((s, i) => (
                            <Reveal key={s.n} delay={i * 0.06} className="bg-[#0E1320] p-6">
                                <p className="font-mono2 text-xs text-amber-400">{s.n}</p>
                                <h3 className="mt-3 font-display font-bold text-white">{s.name}</h3>
                                <p className="mt-2 text-xs text-slate-400 leading-relaxed">{s.text}</p>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15}>
                        <Link to="/process" data-testid="home-process-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-violet-300 hover:text-violet-200">
                            See the full process <ArrowRight className="h-4 w-4" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* Why choose us */}
            <section className="py-20 lg:py-28 border-t border-white/5" data-testid="home-why-us">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-14">
                    <Reveal>
                        <Eyebrow>Why Innovative Fusion Hives</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white">Built different, on purpose.</h2>
                        <img src={IMAGES.workspace} alt="Agency workspace with creative technology" className="mt-8 rounded-2xl border border-white/10 aspect-video object-cover w-full" loading="lazy" />
                    </Reveal>
                    <div className="grid gap-5 content-center">
                        {[
                            { t: "One team, every discipline", d: "Design, development, social, and marketing under one roof — no hand-offs lost between vendors." },
                            { t: "Strategy before pixels", d: "Every project starts with your business goals, so the creative work has a job to do." },
                            { t: "Honest scopes and timelines", d: "Clear deliverables and pricing agreed up front. Advertising budgets and platform fees are always separate and transparent." },
                            { t: "Local roots, modern craft", d: "Based in Long Beach, California — available in person, fluent in the latest web technology." },
                        ].map((r, i) => (
                            <Reveal key={r.t} delay={i * 0.07}>
                                <div className="rounded-2xl border border-white/10 bg-hive-card p-6 transition-colors hover:border-violet-500/40">
                                    <h3 className="font-display font-bold text-white">{r.t}</h3>
                                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{r.d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Inquiry CTA + form */}
            <section id="start-project" className="py-20 lg:py-32 border-t border-white/5 bg-[radial-gradient(50%_60%_at_50%_0%,rgba(124,58,237,0.12),transparent_70%)]" data-testid="home-inquiry-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 grid lg:grid-cols-2 gap-14">
                    <Reveal>
                        <Eyebrow>Start Your Project</Eyebrow>
                        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
                            Tell us what you are building. We will bring the hive.
                        </h2>
                        <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-lg">
                            Share a few details about your project and we will respond during business hours with next steps. Every inquiry receives a reference number you can follow up on.
                        </p>
                        <div className="mt-10 space-y-4">
                            <div className="flex items-start gap-3 text-sm text-slate-300">
                                <MapPin className="h-5 w-5 shrink-0 text-violet-400" />
                                <a href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="home-address-link" className="hover:text-white transition-colors">
                                    {BUSINESS.address1}, {BUSINESS.address2}
                                </a>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-300">
                                <Clock className="h-5 w-5 shrink-0 text-violet-400" />
                                <span data-testid="home-business-hours">{BUSINESS.hours}</span>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="rounded-2xl border border-white/10 bg-hive-card p-7 sm:p-9">
                            <LeadForm compact />
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
